import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useContext,
  useMemo,
} from 'react';
import {
  Application,
  useApplications,
  defaultApplication,
  ApplicationStageBackward,
} from '.';
import { WithdrawalReason, WithdrawReasonType } from '../../api/useAccountApi';

export type ApplicationsContextType = {
  applications: Application[],
  setApplicationsData: Dispatch<SetStateAction<Application[]>>,
  fetchApplications(): Promise<void>,
  updateUnderwriterOwner(id: string, ownerId: string, ownerName: string | null): Promise<void>,
  reopenApplication(id: string): Promise<void>,
  getRejectionReasons(): Promise<WithdrawalReason[]>,
  getWithdrawalReasons(): Promise<WithdrawalReason[]>,
  rejectApplication(id: string, rejectReason: WithdrawReasonType): Promise<Application>,
  withdrawApplication(id: string, rejectReason: WithdrawReasonType): Promise<Application>,
  changeApplicationStatus(id: string, stage: ApplicationStageBackward): Promise<void>,
}

const defaultApplicationsContext = {
  applications: [],
  setApplicationsData: () => {},
  fetchApplications: async () => {},
  updateUnderwriterOwner: async () => {},
  reopenApplication: async () => {},
  getRejectionReasons: async () => [],
  getWithdrawalReasons: async () => [],
  rejectApplication: async () => defaultApplication,
  withdrawApplication: async () => defaultApplication,
  changeApplicationStatus: async () => {},
};

export const ApplicationsContext = createContext<ApplicationsContextType>(defaultApplicationsContext);

export function useApplicationsContext() {
  return useContext(ApplicationsContext);
}

export function ApplicationsProvider({ children }: PropsWithChildren) {
  const applications = useApplications();

  const contextValue = useMemo(() => ({ ...applications }), [applications]);

  return (
    <ApplicationsContext.Provider value={contextValue}>
      {children}
    </ApplicationsContext.Provider>
  );
}
